import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { designTokens } from '../../design-system/tokens';

/**
 * Dream TMS 헤더 컴포넌트의 Props 인터페이스
 */
export interface HeaderProps {
  /** 사용자 이름 */
  userName?: string;
  /** 로고 이미지 URL */
  logoUrl?: string;
  /** 로고 클릭 핸들러 */
  onLogoClick?: () => void;
  /** 로그인 버튼 클릭 핸들러 */
  onLoginClick?: () => void;
  /** 설정 클릭 핸들러 */
  onSettingsClick?: () => void;
  /** 로그아웃 클릭 핸들러 */
  onLogoutClick?: () => void;
  /** 사용자 프로필 클릭 핸들러 */
  onProfileClick?: () => void;
  /** 헤더 고정 여부 */
  position?: 'fixed' | 'sticky' | 'static' | 'absolute' | 'relative';
  /** 인증 상태 (로그인 여부) */
  isAuthenticated?: boolean;
}

/**
 * Dream Logistics TMS 관리자 헤더 컴포넌트
 *
 * Dream TMS 브랜드 로고와 사용자 메뉴를 제공하는 GNB (Global Navigation Bar) 컴포넌트입니다.
 * 80px 높이의 고정 헤더로 구성되며, 반응형 디자인을 통해 모바일 환경을 지원합니다.
 *
 * 주요 구성:
 * - 좌측: Dream TMS 로고 + 브랜드 텍스트
 * - 우측: 사용자 인사말 + 프로필/설정/로그아웃 아이콘 버튼
 *
 * 주요 기능:
 * - 클릭 가능한 브랜드 로고 (홈 이동)
 * - 사용자 드롭다운 메뉴 (프로필, 설정, 로그아웃)
 * - 반응형 디자인 (모바일: 브랜드 텍스트 숨김)
 * - 고정 헤더 지원 (fixed/sticky/static)
 * - Material-UI 디자인 토큰 기반 스타일링
 *
 * @component
 * @example
 * // 기본 헤더
 * <Header
 *   userName="홍길동"
 *   onLogoClick={() => navigate('/')}
 *   onSettingsClick={() => navigate('/settings')}
 *   onLogoutClick={handleLogout}
 *   onProfileClick={() => navigate('/profile')}
 * />
 *
 * @example
 * // 커스텀 로고가 있는 헤더
 * <Header
 *   userName="김철수"
 *   logoUrl="/assets/company-logo.png"
 *   onLogoClick={() => navigate('/')}
 *   onSettingsClick={handleSettings}
 *   onLogoutClick={handleLogout}
 * />
 *
 * @example
 * // Sticky 헤더 (스크롤 시 고정)
 * <Header
 *   userName="이영희"
 *   position="sticky"
 *   onLogoClick={() => navigate('/')}
 *   onSettingsClick={() => navigate('/settings')}
 *   onLogoutClick={handleLogout}
 * />
 *
 * @example
 * // Static 헤더 (고정되지 않음)
 * <Header
 *   userName="박민수"
 *   position="static"
 *   onLogoClick={() => navigate('/')}
 *   onProfileClick={() => navigate('/my-profile')}
 *   onSettingsClick={() => navigate('/admin/settings')}
 *   onLogoutClick={() => {
 *     auth.signOut();
 *     navigate('/login');
 *   }}
 * />
 *
 * @example
 * // MainLayout과 함께 사용
 * function MainLayout({ children }) {
 *   const { user } = useAuth();
 *   const navigate = useNavigate();
 *
 *   return (
 *     <Box>
 *       <Header
 *         userName={user?.name}
 *         onLogoClick={() => navigate('/')}
 *         onSettingsClick={() => navigate('/settings')}
 *         onLogoutClick={() => {
 *           auth.logout();
 *           navigate('/login');
 *         }}
 *         onProfileClick={() => navigate('/profile')}
 *       />
 *       <Container sx={{ mt: 2 }}>
 *         {children}
 *       </Container>
 *     </Box>
 *   );
 * }
 */
export const Header: React.FC<HeaderProps> = ({
  userName = '이드림',
  logoUrl = '/assets/images/header_logo.png',
  onLogoClick,
  onLoginClick,
  onSettingsClick,
  onLogoutClick,
  onProfileClick,
  position = 'fixed',
  isAuthenticated = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <AppBar
        position={position}
        sx={{
          backgroundColor: designTokens.colors.brand.primary, // #003f5f - Figma 디자인 브랜드 컬러
          boxShadow: 'none',
          borderBottom: `1px solid ${designTokens.colors.brand.primary}`,
          zIndex: theme.zIndex.drawer + 1,
          width: '100%',
          maxWidth: '100vw',
        }}
      >
        <Toolbar
          sx={{
            minHeight: 80,
            px: { xs: 2, sm: 3 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            maxWidth: '100vw',
          }}
        >
          {/* 로고 및 브랜드 영역 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
            }}
            onClick={onLogoClick}
          >
            {/* Dream Logistics 로고 */}
            <Box
              sx={{
                width: 80,
                height: 80,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Dream Logistics"
                  style={{
                    width: '80px',
                    height: '80px',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '16px',
                    }}
                  >
                    DL
                  </Typography>
                </Box>
              )}
            </Box>

            {/* DREAM TMS + blue yonder 텍스트 (브랜드 강조: blue yonder 만 light blue) */}
            {!isMobile && (
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: 'white',
                  fontFamily: designTokens.typography.fontFamily.primary,
                  fontWeight: designTokens.typography.fontWeight.bold,
                  fontSize: '30px',
                  letterSpacing: '-0.6px',
                  lineHeight: 'normal',
                }}
              >
                DREAM TMS{' '}
                <Box
                  component="span"
                  sx={{
                    color: '#5BC0EB', // Blue Yonder 브랜드 강조 — 어두운 teal 배경 대비 light blue
                  }}
                >
                  blue yonder
                </Box>
              </Typography>
            )}
          </Box>

          {/* 사용자 정보 및 메뉴 영역 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {isAuthenticated ? (
              <>
                {/* 사용자 인사말 */}
                {!isMobile && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      fontSize: '16px',
                      fontFamily: designTokens.typography.fontFamily.primary,
                      letterSpacing: '-0.32px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span style={{ fontWeight: designTokens.typography.fontWeight.bold }}>
                      {userName}님
                    </span>{' '}
                    <span style={{ fontWeight: designTokens.typography.fontWeight.normal }}>
                      안녕하세요
                    </span>
                  </Typography>
                )}

                {/* 설정 아이콘 */}
                <IconButton
                  onClick={onSettingsClick}
                  sx={{
                    color: 'white',
                    p: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>

                {/* 로그아웃 아이콘 */}
                <IconButton
                  onClick={onLogoutClick}
                  sx={{
                    color: 'white',
                    p: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </>
            ) : (
              <>
                {/* 로그인 버튼 - onLoginClick이 있을 때만 표시 */}
                {onLoginClick && (
                  <IconButton
                    onClick={onLoginClick}
                    sx={{
                      color: 'white',
                      p: 1,
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    <LoginIcon fontSize="medium" />
                  </IconButton>
                )}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* 고정 헤더를 위한 스페이서 */}
      {position === 'fixed' && <Box sx={{ height: 80 }} />}
    </>
  );
};

export default Header;
